import React from "react";
import {
  Box, Text, Flex, Center, VStack, HStack,
  Icon, SimpleGrid, Spinner,
} from "@chakra-ui/react";
import {
  FiMapPin, FiCalendar, FiHash, FiShoppingBag, FiTag, FiPackage,
} from "react-icons/fi";
import { StoreImagesPanel } from "./StoreImagesPanel";
import { IneligibleProductsList } from "./IneligibleProductsList";

export function AbstractView({
  loadingStoreDetails,
  selectedStoreDetails,
  selectedStoreImages,
  currentImageIndex,
  setCurrentImageIndex,
  imageRotation,
  setImageRotation,
  onDownload,
  isCocaColaUser,
  productSummary,
  scoreBreakdown,
}) {
  const ineligibleProducts = (productSummary || [])
    .filter((p) => p?.isEligibleForScoring === "N")
    .map((p) => ({
      productName: p?.product?.productName ?? "-",
      productCount: p?.productCount ?? 0,
    }));
  if (loadingStoreDetails) {
    return (
      <Center height="500px">
        <VStack spacing={3}>
          <Spinner size="xl" color="red.500" thickness="4px" speed="0.65s" />
          <Text color="gray.500" fontSize="sm">Loading store details...</Text>
        </VStack>
      </Center>
    );
  }

  if (!selectedStoreDetails) {
    return (
      <Center height="500px">
        <Text color="gray.600">Select a store to view details</Text>
      </Center>
    );
  }

  const scoreCards = [
    {
      key: "availability", label: "Availability", color: "green",
      value: selectedStoreDetails?.availabilityScore,
      max: scoreBreakdown?.availability?.maxAvailabilityScore,
      primary: true,
    },
    {
      key: "activation", label: "Activation", color: "yellow",
      value: selectedStoreDetails?.activationScore,
      max: scoreBreakdown?.activation?.maxActivationScore,
    },
    {
      key: "cooler", label: "Cooler", color: "red",
      value: selectedStoreDetails?.coolerScore,
      max: scoreBreakdown?.cooler?.maxScore,
    },
    {
      key: "redScore", label: "RedScore", color: "blue",
      value: selectedStoreDetails?.redScore,
    },
    {
      key: "soviScore", label: "SoviScore", color: "orange",
      value: selectedStoreDetails?.soviScore,
    },
    {
      key: "coolerBonus", label: "Cooler Bonus", color: "purple",
      value: selectedStoreDetails?.coolerBonus,
    },
  ];

  return (
    <Box height="100%" overflowY="auto">
      {/* Compact store info header */}
      <Box
        mb={4} p={4} bg="white" borderRadius="xl" border="1px solid"
        borderColor="gray.200" boxShadow="sm"
      >
        <Flex align="center" mb={3}>
          <Icon as={FiShoppingBag} color="red.600" boxSize={5} mr={2} />
          <Text fontSize="sm" fontWeight="semibold" color="gray.800">
            {selectedStoreDetails?.storeName || "-"}
          </Text>
        </Flex>

        <SimpleGrid columns={2} spacingX={8} spacingY={2} maxW="520px">
          <HStack spacing={2}>
            <Icon as={FiTag} color="green.600" boxSize={4} />
            <Text fontSize="xs" color="gray.500" minW="70px">Store Type</Text>
            <Text fontSize="xs" fontWeight="semibold" color="gray.800">
              {selectedStoreDetails?.storeType || "-"}
            </Text>
            <Text fontSize="xs" color="gray.800">
               -
            </Text>
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              {selectedStoreDetails?.storeClass || "-"}
            </Text>
          </HStack>
          <HStack spacing={2}>
            <Icon as={FiHash} color="gray.500" boxSize={4} />
            <Text fontSize="xs" color="gray.500" minW="70px">Store ID</Text>
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              {selectedStoreDetails?.storeId ?? "-"} 
            </Text>
          </HStack>
          <HStack spacing={2}>
            <Icon as={FiPackage} color="purple.600" boxSize={4} />
            <Text fontSize="xs" color="gray.500" minW="70px">Auditor Name</Text>
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              {selectedStoreDetails?.auditorName || "-"}
            </Text>
          </HStack>
          <HStack spacing={2}>
            <Icon as={FiCalendar} color="blue.600" boxSize={4} />
            <Text fontSize="xs" color="gray.500" minW="70px">Audited Date</Text>
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              {selectedStoreDetails?.auditedDate || "-"}
            </Text>
          </HStack>
        </SimpleGrid>

        {selectedStoreDetails?.address && (
          <HStack spacing={2} mt={2}>
            <Icon as={FiMapPin} color="orange.600" boxSize={4} />
            <Text fontSize="xs" color="gray.500" minW="70px" flexShrink={0}>Address</Text>
            <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1}>
              {selectedStoreDetails.address}
            </Text>
          </HStack>
        )}
      </Box>

      {/* Score summary cards */}
      <Box mb={5}>
        <Text
          fontSize="xs" fontWeight="bold" color="gray.500" mb={3}
          textTransform="uppercase" letterSpacing="wide"
        >
          Score Summary
        </Text>
        <SimpleGrid columns={3} spacing={3}>
          {scoreCards.map(({ key, label, value, max, color, primary }) => (
            <Box
              key={key}
              p={3}
              bg={`${color}.50`}
              border="1px solid"
              borderColor={`${color}.200`}
              borderRadius="lg"
              textAlign="center"
              boxShadow={primary ? "md" : "none"}
            >
              <Text fontSize="xs" color={`${color}.700`} fontWeight="medium" noOfLines={1}>
                {label}
              </Text>
              <Text fontSize={primary ? "2xl" : "lg"} fontWeight="bold" color={`${color}.600`}>
                {value ?? 0}
                {max ? (
                  <Text as="span" fontSize="xs" color="gray.500" fontWeight="normal">
                    {" "}/{max}
                  </Text>
                ) : null}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* Store Images */}
      <Box>
        <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={4}>
          Store Images
        </Text>
        <StoreImagesPanel
          selectedStoreImages={selectedStoreImages}
          currentImageIndex={currentImageIndex}
          setCurrentImageIndex={setCurrentImageIndex}
          imageRotation={imageRotation}
          setImageRotation={setImageRotation}
          onDownload={onDownload}
        />
      </Box>

      {isCocaColaUser && (
        <Box mt={6}>
          <IneligibleProductsList products={ineligibleProducts} />
        </Box>
      )}
    </Box>
  );
}
